import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  getMultiSelectionFromSearchParams,
  getMultiSelectionUrlUpdates,
  hasExplicitMultiSelectionParams,
  isEmptyMultiSelection,
  normalizeMultiSelection,
  readMultiSelectionFromSession,
  readSingleStoreSelectionFromSession,
  STORE_INSIGHTS_SINGLE_STORE_SELECTION_SESSION_KEY,
  type StoreInsightsMultiSelectionState,
  writeMultiSelectionToSession,
  writeSharedSingleSelectionFromMultiStore,
} from "@/pages/store-insights/shared/storeInsightsSelectionSession";
import { useStoreInsightsLookups } from "@/pages/store-insights/shared/useStoreInsightsLookups";
import { type Store } from "@/services/features/StoreService";

interface UseStoreInsightsPageSelectionOptions {
  sessionKey: string;
  includeUngroupedGroups?: boolean;
  fallbackSingleStoreSessionKey?: string | null;
  defaultToAllGroupsWhenUninitialized?: boolean;
  syncSharedSelection?: boolean;
}

const areNumberArraysEqual = (left: number[], right: number[]) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const areStringArraysEqual = (left: string[], right: string[]) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const getStoreGroup = (store: Store) => ((store as any).group as string | undefined) ?? "";

/** Shared multi-store customer, group, and store selection state. */
export const useStoreInsightsMultiStoreSelection = ({
  sessionKey,
  includeUngroupedGroups = true,
  fallbackSingleStoreSessionKey = STORE_INSIGHTS_SINGLE_STORE_SELECTION_SESSION_KEY,
  defaultToAllGroupsWhenUninitialized = false,
  syncSharedSelection = true,
}: UseStoreInsightsPageSelectionOptions) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    user,
    isCraveUser,
    stores: visibleStores,
    customers,
    loading,
  } = useStoreInsightsLookups();
  const [selectionLoading, setSelectionLoading] = useState(true);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<number[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [selectedStores, setSelectedStores] = useState<Store[]>([]);

  const lastInternalSearchRef = useRef<string | null>(null);
  const hasInitializedSelectionRef = useRef(false);
  const selectionRef = useRef<StoreInsightsMultiSelectionState>({
    customerIds: [],
    groups: [],
    storeIds: [],
  });
  const currentSearch = searchParams.toString();
  const hasPendingInternalSearchUpdate = lastInternalSearchRef.current !== null;

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      const params = new URLSearchParams(searchParams);
      let changed = false;

      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          if (params.has(key)) {
            params.delete(key);
            changed = true;
          }
        } else if (params.get(key) !== value) {
          params.set(key, value);
          changed = true;
        }
      });

      if (!changed) {
        return;
      }

      lastInternalSearchRef.current = params.toString();
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    if (loading) {
      setSelectionLoading(true);
      return;
    }

    const currentSelection: StoreInsightsMultiSelectionState = selectionRef.current;
    const sessionSelection = readMultiSelectionFromSession(sessionKey);
    const hasStoredSelection = !isEmptyMultiSelection(sessionSelection);
    const fallbackSingleStoreSelection = fallbackSingleStoreSessionKey
      ? readSingleStoreSelectionFromSession(fallbackSingleStoreSessionKey)
      : null;
    const fallbackSingleStoreId = fallbackSingleStoreSelection?.storeId ?? null;
    const shouldDefaultToAllGroups =
      defaultToAllGroupsWhenUninitialized &&
      !hasPendingInternalSearchUpdate &&
      !hasExplicitMultiSelectionParams(searchParams) &&
      !hasStoredSelection &&
      !hasInitializedSelectionRef.current &&
      fallbackSingleStoreId === null;
    const defaultGroupSelection = shouldDefaultToAllGroups
      ? Array.from(
          new Set(
            visibleStores
              .map((store) => getStoreGroup(store))
              .filter((group) => includeUngroupedGroups || group !== ""),
          ),
        ).sort((left, right) => left.localeCompare(right))
      : [];
    const sourceSelection = hasPendingInternalSearchUpdate
      ? currentSelection
      : hasExplicitMultiSelectionParams(searchParams)
        ? getMultiSelectionFromSearchParams(searchParams)
        : ((hasStoredSelection ? sessionSelection : null) ??
          (!hasInitializedSelectionRef.current && fallbackSingleStoreId !== null
            ? {
                customerIds: [],
                groups: [],
                storeIds: [fallbackSingleStoreId],
              }
            : null) ?? {
            customerIds: [],
            groups: defaultGroupSelection,
            storeIds: [],
          });
    const normalizedSelection = normalizeMultiSelection(
      sourceSelection,
      visibleStores,
      customers,
      includeUngroupedGroups,
    );
    selectionRef.current = normalizedSelection;
    const nextSelectedCustomerIds = normalizedSelection.customerIds;
    const nextSelectedGroups = normalizedSelection.groups;
    const nextSelectedStores = visibleStores.filter((store) =>
      normalizedSelection.storeIds.includes(store.id),
    );

    if (!areNumberArraysEqual(selectedCustomerIds, nextSelectedCustomerIds)) {
      setSelectedCustomerIds(nextSelectedCustomerIds);
    }
    if (!areStringArraysEqual(selectedGroups, nextSelectedGroups)) {
      setSelectedGroups(nextSelectedGroups);
    }
    const currentStoreIds = selectedStores.map((store) => store.id);
    const nextStoreIds = nextSelectedStores.map((store) => store.id);
    if (!areNumberArraysEqual(currentStoreIds, nextStoreIds)) {
      setSelectedStores(nextSelectedStores);
    }

    updateSearchParams(getMultiSelectionUrlUpdates(normalizedSelection));
    writeMultiSelectionToSession(sessionKey, normalizedSelection);
    if (
      syncSharedSelection &&
      !hasPendingInternalSearchUpdate &&
      hasExplicitMultiSelectionParams(searchParams)
    ) {
      writeSharedSingleSelectionFromMultiStore(normalizedSelection.storeIds, visibleStores);
    }
    hasInitializedSelectionRef.current = true;
    setSelectionLoading(false);
  }, [
    customers,
    fallbackSingleStoreSessionKey,
    defaultToAllGroupsWhenUninitialized,
    hasPendingInternalSearchUpdate,
    includeUngroupedGroups,
    loading,
    selectionLoading,
    searchParams,
    selectedCustomerIds,
    selectedGroups,
    selectedStores,
    sessionKey,
    syncSharedSelection,
    updateSearchParams,
    visibleStores,
  ]);

  useEffect(() => {
    if (lastInternalSearchRef.current === currentSearch) {
      lastInternalSearchRef.current = null;
    }
  }, [currentSearch]);

  const customerStores = useMemo(() => {
    if (!selectedCustomerIds.length) {
      return visibleStores;
    }

    const customerIdSet = new Set(selectedCustomerIds);
    return visibleStores.filter((store) => customerIdSet.has(store.customerId));
  }, [selectedCustomerIds, visibleStores]);

  const groups = useMemo(() => {
    const uniqueGroups = Array.from(
      new Set(
        customerStores
          .map((store) => ((store as any).group ?? "") as string)
          .filter((group) => includeUngroupedGroups || group !== ""),
      ),
    );
    uniqueGroups.sort((left, right) => left.localeCompare(right));
    return uniqueGroups;
  }, [customerStores, includeUngroupedGroups]);

  const filteredStores = useMemo(() => {
    if (!selectedGroups.length) {
      return customerStores;
    }

    const selectedGroupSet = new Set(selectedGroups);
    return customerStores.filter((store) =>
      selectedGroupSet.has(((store as any).group as string | undefined) || ""),
    );
  }, [customerStores, selectedGroups]);

  const groupCounts = useMemo(() => {
    const counts: Record<string, number> = {};

    for (const store of customerStores) {
      const key = ((store as any).group as string | undefined) || "";
      if (!includeUngroupedGroups && key === "") {
        continue;
      }
      counts[key] = (counts[key] ?? 0) + 1;
    }

    return counts;
  }, [customerStores, includeUngroupedGroups]);

  const onCustomerChange = useCallback(
    (nextIds: number[]) => {
      const nextCustomerIdSet = new Set(nextIds);
      const nextCustomerStores = nextIds.length
        ? visibleStores.filter((store) => nextCustomerIdSet.has(store.customerId))
        : visibleStores;
      const availableGroups = new Set(
        nextCustomerStores
          .map((store) => getStoreGroup(store))
          .filter((group) => includeUngroupedGroups || group !== ""),
      );
      const nextSelectedGroups = selectedGroups.filter((group) => availableGroups.has(group));
      const nextSelectedGroupSet = new Set(nextSelectedGroups);
      const nextSelectedStores = nextSelectedGroups.length
        ? nextCustomerStores.filter((store) => nextSelectedGroupSet.has(getStoreGroup(store)))
        : nextCustomerStores;

      const nextSelection: StoreInsightsMultiSelectionState = {
        customerIds: nextIds,
        groups: nextSelectedGroups,
        storeIds: nextSelectedStores.map((store) => store.id),
      };
      selectionRef.current = nextSelection;
      setSelectedCustomerIds(nextSelection.customerIds);
      setSelectedGroups(nextSelection.groups);
      setSelectedStores(nextSelectedStores);
      writeMultiSelectionToSession(sessionKey, nextSelection);
      if (syncSharedSelection) {
        writeSharedSingleSelectionFromMultiStore(nextSelection.storeIds, visibleStores);
      }
      updateSearchParams({
        customerId: nextSelection.customerIds.length ? nextSelection.customerIds.join(",") : null,
        group: nextSelection.groups.length ? nextSelection.groups.join(",") : null,
        storeIds: nextSelection.storeIds.length ? nextSelection.storeIds.join(",") : null,
      });
    },
    [
      includeUngroupedGroups,
      selectedGroups,
      sessionKey,
      syncSharedSelection,
      updateSearchParams,
      visibleStores,
    ],
  );

  const onGroupsChange = useCallback(
    (nextGroups: string[]) => {
      const currentCustomerIds = selectionRef.current.customerIds;
      const currentCustomerIdSet = new Set(currentCustomerIds);
      const customerScopedStores = currentCustomerIds.length
        ? visibleStores.filter((store) => currentCustomerIdSet.has(store.customerId))
        : visibleStores;
      const nextSelectedGroupSet = new Set(nextGroups);
      const nextSelectedStores = nextGroups.length
        ? customerScopedStores.filter((store) => nextSelectedGroupSet.has(getStoreGroup(store)))
        : customerScopedStores;

      const nextSelection: StoreInsightsMultiSelectionState = {
        customerIds: currentCustomerIds,
        groups: nextGroups,
        storeIds: nextSelectedStores.map((store) => store.id),
      };
      selectionRef.current = nextSelection;
      setSelectedGroups(nextSelection.groups);
      setSelectedStores(nextSelectedStores);
      writeMultiSelectionToSession(sessionKey, nextSelection);
      if (syncSharedSelection) {
        writeSharedSingleSelectionFromMultiStore(nextSelection.storeIds, visibleStores);
      }
      updateSearchParams({
        customerId: nextSelection.customerIds.length ? nextSelection.customerIds.join(",") : null,
        group: nextSelection.groups.length ? nextSelection.groups.join(",") : null,
        storeIds: nextSelection.storeIds.length ? nextSelection.storeIds.join(",") : null,
      });
    },
    [sessionKey, syncSharedSelection, updateSearchParams, visibleStores],
  );

  const onStoresChange = useCallback(
    (nextStores: Store[]) => {
      const currentCustomerIds = selectionRef.current.customerIds;
      const currentGroups = selectionRef.current.groups;
      const nextCustomerIds =
        currentCustomerIds.length &&
        nextStores.every((store) => currentCustomerIds.includes(store.customerId))
          ? currentCustomerIds
          : [];
      const nextSelectedGroups =
        currentGroups.length &&
        nextStores.every((store) => currentGroups.includes(getStoreGroup(store)))
          ? currentGroups
          : [];

      const nextSelection: StoreInsightsMultiSelectionState = {
        customerIds: nextCustomerIds,
        groups: nextSelectedGroups,
        storeIds: nextStores.map((store) => store.id),
      };
      selectionRef.current = nextSelection;
      setSelectedCustomerIds(nextSelection.customerIds);
      setSelectedGroups(nextSelection.groups);
      setSelectedStores(nextStores);
      writeMultiSelectionToSession(sessionKey, nextSelection);
      if (syncSharedSelection) {
        writeSharedSingleSelectionFromMultiStore(nextSelection.storeIds, visibleStores);
      }
      updateSearchParams({
        customerId: nextSelection.customerIds.length ? nextSelection.customerIds.join(",") : null,
        group: nextSelection.groups.length ? nextSelection.groups.join(",") : null,
        storeIds: nextSelection.storeIds.join(","),
      });
    },
    [sessionKey, syncSharedSelection, updateSearchParams, visibleStores],
  );

  return {
    user,
    isCraveUser,
    stores: visibleStores,
    customers,
    loading,
    selectionLoading,
    selectedCustomerIds,
    selectedGroups,
    selectedStores,
    groups,
    groupCounts,
    filteredStores,
    onCustomerChange,
    onGroupsChange,
    onStoresChange,
  };
};
